import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '@mui/material'
import { useNavigate } from "@reach/router"
import userStore from '../common/userStore'
import useTitle from '../../hooks/title'
import useSchema from '../../hooks/schema'
import Header from '../header/Header'
import AuthHeader from '../layout/AuthHeader'
import classes from './DrumNet.module.scss'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {ReactComponent as Affiliate} from '../../images/affiliate.svg'
import {ReactComponent as Windows} from '../../images/windows.svg'
import {ReactComponent as Mac} from '../../images/mac.svg'
import useWindowSize from '../../hooks/window-size';
import Footer from '../footer/FooterBlack'
import clsx from 'clsx'
import {createApi} from "../common/API";
import layoutStyles from '../layout/Layout.module.scss'
import { scroll } from '../../utils'
import { useLocation } from "@reach/router"

const faqItems = [
    {
        question: 'What is DrumNet?',
        answer: 'DrumNet is an AI-powered drum machine plugin for producers. It uses Deep Resampling technology to create new drum sample variations and includes a built-in sequencer for making patterns inside your DAW.'
    },
    {
        question: 'How does DrumNet generate drum samples?',
        answer: 'DrumNet uses Deep Resampling to create variations from drum sounds and sample layers. This gives you a fast way to generate custom kicks, snares, hats, percussion, and full drum ideas without designing every sound from scratch.'
    },
    {
        question: 'Does DrumNet include a sequencer?',
        answer: 'Yes. DrumNet includes a built-in step sequencer, so you can create simple rhythms or more detailed drum patterns directly in the plugin.'
    },
    {
        question: 'What styles and presets are included with DrumNet?',
        answer: 'DrumNet includes 200 hand-crafted presets covering styles such as House, Disco, R&B, Hip Hop, and more. Presets include individual layers so you can shape the drum sound further.'
    },
    {
        question: 'Which plugin formats does DrumNet support?',
        answer: 'DrumNet is available for Windows and Mac. The Windows installer includes VST3 and AAX, while the Mac installer includes VST3, AU, and AAX.'
    },
    {
        question: 'Can I use DrumNet in my DAW?',
        answer: 'Yes. DrumNet is designed for DAW workflows and works in hosts that support its plugin formats, including VST3, AU on Mac, and AAX.'
    },
    {
        question: 'Can I try DrumNet before buying?',
        answer: 'Yes. You can try all DrumNet features for 14 days. After the trial, import and export are disabled unless you purchase a lifetime license or subscribe to a plan.'
    },
    {
        question: 'What is included with a DrumNet purchase?',
        answer: 'The one-time purchase includes DrumNet only, paid once and used forever. Subscription plans start at $5.99 per month and include DrumNet as part of the Session Loops bundle.'
    }
];

const showApp = createApi('/app/getApps', {method: 'POST', expandData: true, notify: true, noThrow: false});

const DrumNet = () => {
    useTitle("DrumNet — AI Drum Machine & Sample Generator (Free Trial)", "Generate infinite royalty-free drum samples instantly with AI. 200+ presets, built-in sequencer. Free 14-day trial — VST plugin for Windows and Mac.", "/drumnet")
    const [version, setVersion] = useState('');
    const [price, setPrice] = useState('');
    useSchema({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": "DrumNet",
          "description": "AI-powered drum machine plugin with infinite sample generation. Create custom drum samples in any genre using Deep Resampling technology. Includes 200 hand-crafted presets and a built-in sequencer.",
          "url": "https://sessionloops.com/drumnet",
          "applicationCategory": "MusicApplication",
          "operatingSystem": "Windows 10 or later, macOS 10.15 or later",
          "softwareVersion": version || undefined,
          "featureList": [
            "Infinite drum sample variations via Deep Resampling",
            "Built-in step sequencer",
            "200 hand-crafted presets (House, Disco, R&B, Hip Hop and more)",
            "Individual sample layers per preset",
            "VST3, AU, AAX plugin formats",
            "14-day free trial"
          ],
          "offers": [
            {
              "@type": "Offer",
              "name": "One-time Purchase — Limited-Time Offer",
              "price": price || "49",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": "https://sessionloops.com/account/app-buy?app-id=DrumNet"
            },
            {
              "@type": "Offer",
              "name": "Subscription",
              "price": "5.99",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": "https://sessionloops.com/pricing"
            }
          ],
          "publisher": {
            "@type": "Organization",
            "name": "Session Loops",
            "url": "https://sessionloops.com",
            "logo": "https://sessionloops.com/logo.png"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://sessionloops.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "DrumNet",
              "item": "https://sessionloops.com/drumnet"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqItems.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        }
      ]
    })
    const [winUrl, setWinUrl] = useState('');
    const [macUrl, setMacUrl] = useState('');
    const navigate = useNavigate();
    const windowSize = useWindowSize();
    const location = useLocation();
    const [activeFaqIndex, setActiveFaqIndex] = useState(null);
    useEffect(() => {
        //console.log('hash', window.location.hash)
        if (window.location.hash) {
            let dom = document.getElementById(window.location.hash.replace('#', ''));
            dom && dom.scrollIntoView({block:"center", behavior:"smooth"})
        } else {
            window.scrollTo(0, 0)
        }
        const fetchApiData = async () => {
            try {
              const response = await showApp();
              setVersion(response[1].version)
              setPrice(response[1].price)
              setMacUrl(response[1].macUrl)
              setWinUrl(response[1].winUrl)
            } catch (error) {
              console.error('Error fetching API data:', error);
            }
          };
        fetchApiData()
    }, [location.pathname])
    return <div className={clsx(classes.root, layoutStyles.root)}>
      {userStore.isAnonymous ? <Header /> : <AuthHeader />}
        <div className={classes.banner}>
            <h1 className={classes.title}>DrumNet</h1>
            {windowSize.width < 500 ?
            <div className={classes.description1}>AI-powered<br/>drum machine plugin<br/>with<br/>Infinite Sample Generation</div>:
             <React.Fragment>
            <div className={classes.description1}>AI-powered drum machine plugin with</div>
            <div className={classes.description2}>Infinite Sample Generation</div>
            </React.Fragment>}
        </div>
        <div className={classes.options} >
            <div className={clsx(classes.option, classes.purple)}  style={windowSize.width < 500 ? {paddingLeft: 'calc(35vw + 2px)'} : {}}
                onClick = {() => document.getElementById("buys").scrollIntoView({block:"center", behavior:"smooth"})}>
                <ShoppingCartIcon/>
                <div className={classes.optionText} style={windowSize.width < 500 ? {marginLeft : 22} : {}}>Buy Now</div>
            </div>
            <div className={clsx(classes.option, classes.purple)} style={windowSize.width < 500 ? {borderTop: '1.5px solid white', borderBottom: '1.5px solid white'} :
            {borderLeft: '1.5px solid white', borderRight: '1.5px solid white'}}
                onClick = {() => document.getElementById("downloads").scrollIntoView({block:"center", behavior:"smooth"})}
            >
                <SaveAltIcon style={{fontSize: 28}}/>
                <div className={classes.optionText}>{windowSize.width < 500 ? 'Download' : 'Download Free'}</div>
            </div>
            <a className={clsx(classes.option, classes.purple)} style={{textDecoration: 'none'}} href="https://sessionloops.firstpromoter.com/"  target="_blank">
                <Affiliate style={{width: 27, height: 27}}/>
                <div className={classes.optionText}>{windowSize.width < 500 ? 'Affiliates' : 'Affiliate Program'}</div>
            </a>
        </div>
        <div className={classes.content}>
            <div className={classes.feature1}>
                <div className={classes.feature1Title}>What is DrumNet</div>
                <div className={classes.feature1Description}>DrumNet is not just another sample-based drum machine. Using state-of-art AI technology, it allows you to easily create your own drum samples in any music genre and modify them at will, without any technical knowledge. </div>
            </div>
            <div className={classes.feature4}>
                <div className={classes.feature4Title}>Infinite Variations based on your Own Samples</div>
                <div className={classes.feature4Description}>With our innovative Deep Resampling technology, you can now unlock infinite possibilities for creating unique and customized drum sample variations, giving your music a distinct edge and allowing your creativity to soar to new heights.</div>
            </div>
            <div className={classes.feature2}>
                <div className={classes.feature2Title}>Simple, yet Powerful built-in Sequencer</div>
                <div className={classes.feature2Description}>Whether you're looking to create a complex drum beat or a simple rhythm, our intuitive interface dramatically simplifies pattern creation, keeping the hassle at a minimum.</div>
            </div>
            <div className={classes.feature3}>
                <div className={classes.feature3Title}>200 New Groundbreaking Presets</div>
                <div className={classes.feature3Description}>Get started with our collection of 200 brand-new hand-crafted presets. Explore drum sounds of House, Disco, R&B, Hip Hop and lots more  — all coming with individual layers.</div>
            </div>
            <iframe style={windowSize.width < 500 ? {marginTop: 200, transform: 'translate(0, 190px)'} : {marginTop: 400, marginBottom: 100}} className={classes.embedded} width={windowSize.width < 771 ? 345 : 771} height="300" scrolling="no" frameBorder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/1658430088&color=%237070f9&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false"></iframe>
            <div className={classes.free} />
            <div id="downloads" className={classes.downloads}>
                <div className={classes.download}>
                    <Button startIcon={<Windows style={{marginLeft: 7, marginRight: 10}}/>} variant="contained" className={classes.button}  href={winUrl} target="_blank">Download for Windows</Button>
                    Version {version}, VST3 & AAX
                </div>
                <div className={classes.download}>
                    <Button startIcon={<Mac style={{marginLeft: 7, marginRight: 10}}/>} variant="contained" className={classes.button}  href={macUrl} target="_blank">Download for Mac</Button>
                    Version {version}, VST3 & AU & AAX, Universal
                </div>
            </div>
            <div className={classes.text}>You can try out all features for 14 days. Afterwards import & export will be disabled.<br/>To access full version, purchase a lifetime license or subscribe to our plans. </div>
            <div id="buys" className={classes.buys}>
                <div className={classes.buy}>
                    <div className={classes.buyTitle}>One-time<br/>Purchase</div>
                    <div className={classes.buyPrice}>
                        ${price}
                        {/* <div className={classes.priceDiscount}>$99</div>
                        <div className={classes.priceSegment}/>
                        <div className={classes.priceNow}>${price}</div>
                        <div className={classes.priceTag}>Limited-Time<br/>Offer</div> */}
                    </div>
                    <div className={classes.buyDescription}>DrumNet only, pay once, use forever</div>
                    <Button style={{width: 193}} variant="contained" className={classes.button}  onClick={() => navigate('/account/app-buy?app-id=DrumNet')}>Buy DrumNet</Button>
                </div>
                <div className={classes.buy} style={{width: 361, marginLeft: windowSize.width < 500 ? 0 : 93}}>
                    <div className={classes.buyTitle}>Subscription<br/>( 14 days free trial )</div>
                    <div className={classes.buyPrice}>
                        <div style={{color: '#E7E9EF'}} className={classes.priceSubscription}>Starts from</div>
                        <div style={{position: 'static', marginLeft: 10, marginRight: 30}} className={classes.priceNow}>$5.99</div>
                        <div className={classes.priceSubscription}>/ mo</div>
                    </div>
                    <div className={classes.buyDescription}>Everything in a bundle</div>
                    <Button style={{width: 193}} variant="contained" className={classes.button}  onClick={() => navigate('/pricing')}>See Plans</Button>
                </div>
                <div className={classes.buySegment1}/>
                <div className={classes.buySegment2}/>
            </div>
            <div className={classes.faqSection}>
                <div className={classes.faqTitle}>DrumNet FAQ</div>
                <div className={classes.faqList}>
                    {faqItems.map((item, index) => (
                        <div key={item.question} className={classes.faqItem}>
                            <button
                                type="button"
                                className={classes.faqQuestion}
                                aria-expanded={activeFaqIndex === index}
                                aria-controls={`drumnet-faq-${index}`}
                                onClick={() => setActiveFaqIndex(activeFaqIndex === index ? null : index)}
                            >
                                <span>{item.question}</span>
                                <span className={clsx(classes.faqIcon, activeFaqIndex === index && classes.faqIconOpen)} />
                            </button>
                            <div
                                id={`drumnet-faq-${index}`}
                                className={clsx(classes.faqAnswer, activeFaqIndex === index && classes.faqAnswerOpen)}
                            >
                                <p>{item.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
        <Footer />
      </div>
  }

  export default DrumNet
